import { isDefined } from "@clipboard-health/util-ts";
import { Box, Chip, Stack, type Theme } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import {
  FACILITY_CANCELED_ME_REQUEST_STATUS_CHIP_MAP,
  formatShiftStartEndDateTime,
} from "@src/appV2/Chat/utils";
import { PACIFIC_TIME_ZONE } from "@src/appV2/Timezone/constants";
import { useModalState } from "@src/lib/ui-react";

import { Button } from "../../components/Button";
import { DESTRUCTIVE_BUTTON_SX, PRIMARY_BUTTON_SX } from "../../components/Button.styles";
import { Text } from "../../components/Text";
import { useApproveFcmRequest, useRejectFcmRequest } from "../api/useFcmRequestActions";
import { useGetShiftBasicDetails } from "../api/useGetShiftBasicDetails";
import { parseChannelUrl } from "../constants";
import { FCM_APPROVAL_STATUS, type FcmApprovalStatus } from "../utils/parseFcmRequestMessage";
import { FcmApprovalDialog } from "./FcmApprovalDialog";
import { FcmNoShowDialog } from "./FcmNoShowDialog";
import { FcmRejectionDialog } from "./FcmRejectionDialog";
import { FcmWleDialog } from "./FcmWleDialog";

interface FcmRequestMessageProps {
  messageId: string;
  channelUrl: string;
  shiftId: string;
  approvalStatus: FcmApprovalStatus;
  workerName: string;
  description: string | undefined;
  workplaceId: string;
  workplaceUserId?: string;
}

/**
 * Renders a FCM (Facility Canceled Me / Sent Home) request message with rich card UI.
 * Shows request details, status chip, and accept/decline actions.
 */
export function FcmRequestMessage(props: FcmRequestMessageProps) {
  const {
    messageId,
    channelUrl,
    shiftId,
    approvalStatus,
    workerName,
    description,
    workplaceId,
    workplaceUserId,
  } = props;

  const { profile } = useSession();
  const approvalModalState = useModalState();
  const rejectionModalState = useModalState();
  const noShowModalState = useModalState();
  const wleModalState = useModalState();

  // Extract worker ID from channel URL (format: workplaceId_workerId)
  const channelContext = parseChannelUrl(channelUrl);
  const workerId = channelContext?.workerId;

  const { data: shiftResponse } = useGetShiftBasicDetails(
    { shiftId },
    { enabled: isDefined(shiftId) }
  );

  const actionContext = {
    workerId,
    workplaceId,
    workplaceUserId,
  };

  const { mutateAsync: approveMutate, isLoading: isApproving } =
    useApproveFcmRequest(actionContext);
  const { mutateAsync: rejectMutate, isLoading: isRejecting } = useRejectFcmRequest(actionContext);

  const shift = shiftResponse?.response;
  const shiftTimeZone = shift?.facility?.tmz ?? profile?.tmz ?? PACIFIC_TIME_ZONE;
  // Mirror the BE guard in shiftsUnassign.service.ts: NCNS is blocked when the worker
  // has entered the facility geofence OR clocked in. Show WLE option in either case.
  const hasWorkerEnteredGeofence =
    isDefined(shift?.attendance?.arrivedFacilityGeofenceAt) || isDefined(shift?.clockInOut?.start);
  // Defined only when worker entered the geofence: true if they've since left, false if still inside.
  const hasLeftGeofence = isDefined(shift?.attendance?.arrivedFacilityGeofenceAt)
    ? isDefined(shift?.attendance?.leftFacilityGeofenceAt)
    : undefined;

  const formattedShiftTime =
    isDefined(shift?.start) && isDefined(shift?.end)
      ? formatShiftStartEndDateTime(shift.start, shift.end, shiftTimeZone)
      : "";

  const isRequestPending = approvalStatus === FCM_APPROVAL_STATUS.PENDING;
  const isUpdateResponse = approvalStatus !== FCM_APPROVAL_STATUS.PENDING;

  const chipProps = FACILITY_CANCELED_ME_REQUEST_STATUS_CHIP_MAP[approvalStatus];

  const handleApprove = async (reason: string) => {
    await approveMutate({
      shiftId,
      messageId,
      isUpdateResponse,
      reason,
    });
    approvalModalState.closeModal();
  };

  const handleApproveFromRejectionDialog = async (reason: string) => {
    await approveMutate({
      shiftId,
      messageId,
      isUpdateResponse,
      reason,
    });
    rejectionModalState.closeModal();
  };

  const handleReject = async (reason: string, wantsFollowUp: boolean) => {
    await rejectMutate({
      shiftId,
      messageId,
      isUpdateResponse,
      reason,
    });
    rejectionModalState.closeModal();
    if (wantsFollowUp) {
      if (hasWorkerEnteredGeofence) {
        wleModalState.openModal();
      } else {
        noShowModalState.openModal();
      }
    }
  };

  const title = `${workerName} submitted a sent home request`;

  const actionButtons = isRequestPending ? (
    <Stack direction="row" spacing={1} width="100%">
      <Button
        size="small"
        variant="outlined"
        sx={{
          // oxlint-disable-next-line typescript/no-misused-spread
          ...PRIMARY_BUTTON_SX,
          flex: 1,
        }}
        onClick={approvalModalState.openModal}
      >
        Accept
      </Button>
      <Button
        size="small"
        variant="outlined"
        sx={{
          // oxlint-disable-next-line typescript/no-misused-spread
          ...DESTRUCTIVE_BUTTON_SX,
          flex: 1,
        }}
        onClick={rejectionModalState.openModal}
      >
        Decline
      </Button>
    </Stack>
  ) : null;

  return (
    <>
      <Stack
        width="100%"
        direction="row"
        spacing={1}
        alignItems="flex-end"
        sx={{
          justifyContent: "flex-start",
          padding: 2,
        }}
      >
        <Box
          sx={(theme: Theme) => ({
            border: "1px solid",
            borderColor: theme.palette.divider,
            borderRadius: theme.borderRadius?.large,
            maxWidth: "400px",
            flex: 1,
            padding: theme.spacing(2),
          })}
        >
          <Stack direction="row" alignItems="flex-start" gap={2}>
            <Stack sx={{ marginRight: "auto", flex: 1 }}>
              <Text
                semibold
                variant="body2"
                sx={(theme) => ({ color: theme.palette.text.primary })}
              >
                {title}
              </Text>

              {formattedShiftTime && (
                <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
                  {formattedShiftTime}
                </Text>
              )}

              {isDefined(description) && (
                <Text
                  variant="caption"
                  sx={(theme) => ({ color: theme.palette.text.secondary, marginTop: 0.5 })}
                >
                  {description}
                </Text>
              )}
            </Stack>

            <Chip
              label={chipProps.label}
              aria-label={chipProps.label}
              color={chipProps.color}
              variant="outlined"
              size="small"
              sx={{
                border: "none",
                borderRadius: 2,
                fontWeight: "bold",
                ...chipProps.customStyles,
              }}
            />
          </Stack>

          {actionButtons && <Box sx={{ marginTop: 2 }}>{actionButtons}</Box>}
        </Box>
      </Stack>

      <FcmApprovalDialog
        modalState={approvalModalState}
        isUpdateResponse={isUpdateResponse}
        isLoading={isApproving}
        onApprove={handleApprove}
        onCancel={approvalModalState.closeModal}
      />

      <FcmRejectionDialog
        modalState={rejectionModalState}
        isLoading={isRejecting || isApproving}
        workerName={workerName}
        hasWorkerEnteredGeofence={hasWorkerEnteredGeofence}
        onReject={handleReject}
        onApprove={handleApproveFromRejectionDialog}
        onCancel={rejectionModalState.closeModal}
      />

      <FcmNoShowDialog
        modalState={noShowModalState}
        shiftId={shiftId}
        workerId={workerId ?? ""}
        workplaceId={workplaceId}
        shiftStart={shift?.start}
        canRequestReplacement={shift?.canRequestReplacement}
        geofenceEntryTime={shift?.attendance?.arrivedFacilityGeofenceAt ?? undefined}
        timezone={shiftTimeZone}
        onSuccess={noShowModalState.closeModal}
        onCancel={noShowModalState.closeModal}
      />

      <FcmWleDialog
        modalState={wleModalState}
        shiftId={shiftId}
        hasLeftGeofence={hasLeftGeofence}
        timezone={shiftTimeZone}
        onSuccess={wleModalState.closeModal}
        onCancel={wleModalState.closeModal}
      />
    </>
  );
}
